import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import MedicationIcon from '@mui/icons-material/Medication';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate, useParams } from 'react-router-dom';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';
import { formatDateTimeDisplay } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

function formatDate(value) {
  return formatDateTimeDisplay(value);
}

function formatMoney(value) {
  const number = Number(value || 0);
  return `${number.toLocaleString('en-US', { maximumFractionDigits: 2 })} SAR`;
}

function approvalMeta(value, t) {
  const status = Number(value);
  if (status === 1) {
    return { label: t('approved') || 'Approved', color: COLORS.green, bg: COLORS.greenSoft };
  }
  if (status === 2) {
    return { label: t('rejected') || 'Rejected', color: COLORS.rose, bg: COLORS.roseSoft };
  }
  if (status === 3) {
    return { label: t('needs_edit') || 'Needs edit', color: COLORS.warning, bg: COLORS.warningSoft };
  }
  return { label: t('under_review') || 'Under review', color: COLORS.gold, bg: COLORS.goldSoft };
}

export default function RecordDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { record_id: recordId } = useParams();
  const [recordData, setRecordData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const isManager = Number(localStorage.getItem('role_id') || 0) === 1;

  const fetchRecordDetails = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiUrl}/vetclinic-records/${recordId}`);
      const nextRecord = { ...data };
      nextRecord.file_uploads = typeof nextRecord.file_uploads === 'string'
        ? nextRecord.file_uploads.split(';').filter(Boolean)
        : Array.isArray(nextRecord.file_uploads)
          ? nextRecord.file_uploads
          : [];

      if (nextRecord.owner_id) {
        try {
          const ownerResponse = await axios.get(`${apiUrl}/owners/${nextRecord.owner_id}`);
          const owner = ownerResponse.data || {};
          nextRecord.owner_name = `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || nextRecord.OwnerName;
          nextRecord.owner_phone = owner.phone || '-';
        } catch {
          nextRecord.owner_name = nextRecord.OwnerName || '-';
          nextRecord.owner_phone = '-';
        }
      } else {
        nextRecord.owner_name = nextRecord.OwnerName || '-';
        nextRecord.owner_phone = '-';
      }

      setRecordData(nextRecord);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load record details');
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    fetchRecordDetails();
  }, [fetchRecordDetails]);

  const images = useMemo(() => (
    recordData?.file_uploads?.length
      ? recordData.file_uploads.map((file) => ({
          original: `${apiUrl}/uploads/${file}`,
          thumbnail: `${apiUrl}/uploads/${file}`,
        }))
      : []
  ), [recordData]);

  const updateApproval = async (approval) => {
    if (!recordData || !isManager) return;
    setApprovalSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${apiUrl}/vetclinic-records/${recordData.record_id}/approval`,
        { approval },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      setRecordData((current) => ({ ...current, approval }));
      setMessage(t('saved') || 'Saved');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update approval status');
    } finally {
      setApprovalSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', bgcolor: COLORS.page }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!recordData) {
    return (
      <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
        <Alert severity="error">{error || t('no_data') || 'No data available'}</Alert>
      </Box>
    );
  }

  const approval = approvalMeta(recordData.approval, t);
  const proceduresTotal = (recordData.procedures || []).reduce((sum, item) => sum + Number(item.price || 0), 0);
  const medicationsTotal = (recordData.medications || []).reduce((sum, item) => sum + Number(item.price || 0), 0);

  return (
    <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 64, height: 64, bgcolor: COLORS.blueSoft, color: COLORS.blue }}>
              <HealthAndSafetyIcon />
            </Avatar>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                  {t('clinic_record_details') || 'Clinic record details'}
                </Typography>
                <Chip label={approval.label} sx={{ bgcolor: approval.bg, color: approval.color, fontWeight: 900 }} />
              </Stack>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                #{recordData.record_id} · <span className="date-ltr">{formatDate(recordData.record_date)}</span>
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/recordlist')} sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('back') || 'Back'}
            </Button>
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              onClick={() => navigate(`/VetClinicRecordEdit/${recordData.record_id}`)}
              sx={{ borderColor: COLORS.line, color: COLORS.gold, fontWeight: 900 }}
            >
              {t('edit') || 'Edit'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {isManager && (
        <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2.5, borderColor: approval.color }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
            <Box>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('approval_actions') || 'Approval actions'}
              </Typography>
              <Typography sx={{ color: COLORS.muted }}>
                {t('approval_actions_hint') || 'Manager-only controls for sending the record to accounting and reports.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button disabled={approvalSaving || Number(recordData.approval) === 1} startIcon={<CheckCircleIcon />} onClick={() => updateApproval(1)} sx={{ color: COLORS.green, fontWeight: 900 }}>
                {t('approve') || 'Approve'}
              </Button>
              <Button disabled={approvalSaving || Number(recordData.approval) === 3} startIcon={<AutorenewRoundedIcon />} onClick={() => updateApproval(3)} sx={{ color: COLORS.warning, fontWeight: 900 }}>
                {t('request_edit') || 'Request edit'}
              </Button>
              <Button disabled={approvalSaving || Number(recordData.approval) === 2} startIcon={<CancelIcon />} onClick={() => updateApproval(2)} sx={{ color: COLORS.rose, fontWeight: 900 }}>
                {t('reject') || 'Reject'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <Metric icon={<ReceiptLongIcon />} label={t('record_id') || 'Record ID'} value={`#${recordData.record_id}`} helper={<span className="date-ltr">{formatDate(recordData.record_date)}</span>} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Metric icon={<HealthAndSafetyIcon />} label={t('horse_name') || 'Horse'} value={recordData.horse_name || '-'} helper={`RFID ${recordData.rfid_chip_number || '-'}`} color={COLORS.blue} soft={COLORS.blueSoft} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Metric icon={<PersonIcon />} label={t('owner_name') || 'Owner'} value={recordData.owner_name || '-'} helper={recordData.owner_phone || '-'} color={COLORS.green} soft={COLORS.greenSoft} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Metric icon={<PaymentsIcon />} label={t('total_amount') || 'Total amount'} value={formatMoney(recordData.total_amount)} helper={recordData.payment_type_name || '-'} color={COLORS.gold} soft={COLORS.goldSoft} />
        </Grid>

        <Grid item xs={12} md={7}>
          <Section title={t('medical_summary') || 'Medical summary'}>
            <InfoBlock title={t('medical_history') || 'Medical history'} value={recordData.medical_history} />
            <InfoBlock title={t('diagnosis') || 'Diagnosis'} value={recordData.diagnosis} />
            <InfoBlock title={t('treatment') || 'Treatment'} value={recordData.treatment} />
            <InfoBlock title={t('notes') || 'Notes'} value={recordData.notes} />
          </Section>
        </Grid>

        <Grid item xs={12} md={5}>
          <Section title={t('clinical_services') || 'Clinical services'}>
            <ListBlock title={t('procedures') || 'Procedures'} total={proceduresTotal} rows={recordData.procedures} getName={(row) => row.procedure_name} getMeta={(row) => formatMoney(row.price)} empty={t('no_procedures_selected') || 'No procedures selected'} />
            <Divider sx={{ my: 2, borderColor: COLORS.line }} />
            <ListBlock title={t('medications') || 'Medications'} total={medicationsTotal} rows={recordData.medications} getName={(row) => row.medication_name} getMeta={(row) => `${row.dosage || '-'} ${row.unit || ''} · ${row.quantity || 0}${row.duration ? ` · ${row.duration}` : ''} · ${formatMoney(row.price)}`} empty={t('no_medications_provided') || 'No medications provided'} icon={<MedicationIcon />} />
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title={t('doctor') || 'Doctor'}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: COLORS.goldSoft, color: COLORS.gold }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{recordData.doctor_name || '-'}</Typography>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{t('responsible_user') || 'Responsible user'}</Typography>
              </Box>
            </Stack>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title={t('attachments') || 'Attachments'}>
            {images.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {images.map((image, index) => (
                  <Tooltip title={t('view') || 'View'} key={image.thumbnail}>
                    <Box
                      component="img"
                      src={image.thumbnail}
                      alt={`Uploaded ${index + 1}`}
                      onClick={() => {
                        setPhotoIndex(index);
                        setIsOpen(true);
                      }}
                      sx={{
                        width: 96,
                        height: 96,
                        objectFit: 'cover',
                        border: `1px solid ${COLORS.line}`,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ color: COLORS.muted }}>{t('no_attachments') || 'No attachments'}</Typography>
            )}
          </Section>
        </Grid>
      </Grid>

      {isOpen && images.length > 0 && (
        <Lightbox
          mainSrc={images[photoIndex].original}
          nextSrc={images[(photoIndex + 1) % images.length].original}
          prevSrc={images[(photoIndex + images.length - 1) % images.length].original}
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() => setPhotoIndex((photoIndex + images.length - 1) % images.length)}
          onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % images.length)}
        />
      )}
    </Box>
  );
}

function Metric({ icon, label, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 120 }}>
      <Stack direction="row" spacing={1.4} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, overflowWrap: 'anywhere' }}>{value}</Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function Section({ title, children }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, height: '100%' }}>
      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1.5 }}>{title}</Typography>
      {children}
    </Paper>
  );
}

function InfoBlock({ title, value }) {
  return (
    <Box sx={{ p: 1.5, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft, mb: 1.2 }}>
      <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 900 }}>{title}</Typography>
      <Typography sx={{ color: COLORS.text, whiteSpace: 'pre-wrap' }}>{value || '-'}</Typography>
    </Box>
  );
}

function ListBlock({ title, total, rows, getName, getMeta, empty, icon = <HealthAndSafetyIcon /> }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ width: 32, height: 32, bgcolor: COLORS.blueSoft, color: COLORS.blue }}>{icon}</Avatar>
          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{title}</Typography>
        </Stack>
        <Chip size="small" label={formatMoney(total)} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 900 }} />
      </Stack>
      <Stack spacing={1}>
        {rows?.length ? rows.map((row, index) => (
          <Box key={`${getName(row)}-${index}`} sx={{ p: 1, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.card }}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography sx={{ color: COLORS.text, fontWeight: 800 }}>{getName(row) || '-'}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{getMeta(row)}</Typography>
            </Stack>
          </Box>
        )) : <Typography sx={{ color: COLORS.muted }}>{empty}</Typography>}
      </Stack>
    </Box>
  );
}
